import React, { useState, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import { useReturnManifestDetailContext } from 'providers/ReturnManifestDetailProvider';

import Table from 'components/common/Table';
import ReturnManifestShipmentExpandedRow from 'components/add_return/return-manifest/ReturnManifestDetail/ReturnManifestShipment/ReturnManifestShipmentExpandedRow';
import ReturnManifestShipmentSummaryPanel from 'components/add_return/return-manifest/ReturnManifestDetail/ReturnManifestShipment/ReturnManifestShipmentSummaryPanel';
import UnsavedChangesModal from 'components/add_return/return-manifest/ReturnManifestDetail/UnsavedChangesModal';

import { returnManifestApi } from 'api';
import { RETURN_MANIFEST_DETAIL_SHIPMENT_COLUMNS } from 'constants/returnManifest';

const ReturnManifestShipment = ({
  returnManifestId,
  onReturnManifestUpdate,
}) => {
  const tableRef = useRef(null);
  const [totalShipments, setTotalShipments] = useState(0);

  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const { returnManifestStatus, finishEditing, editingShipments } =
    useReturnManifestDetailContext();

  const refreshReturnManifestData = useCallback(() => {
    tableRef.current?.refetch();
    onReturnManifestUpdate?.();
  }, [onReturnManifestUpdate]);

  const handleDataUpdate = useCallback((data, totalItems) => {
    setTotalShipments(totalItems);
  }, []);

  const handleRemoveShipment = useCallback(
    async (returnManifestId, shipmentId) => {
      try {
        const response =
          await returnManifestApi.removeShipmentFromReturnManifest(
            returnManifestId,
            shipmentId,
          );

        finishEditing(shipmentId);

        toast.success('Removed shipment from return manifest');
        refreshReturnManifestData();
      } catch (error) {
        toast.error('Failed to remove shipment from return manifest');
      }
    },
    [finishEditing, refreshReturnManifestData],
  );

  const checkAndRemoveShipment = useCallback(
    (returnManifestId, shipmentId) => {
      const editingShipmentIds = Object.keys(editingShipments);
      const hasOtherUnsavedChanges = editingShipmentIds.some(
        id => id !== shipmentId.toString() && editingShipments[id],
      );

      if (hasOtherUnsavedChanges) {
        setShowUnsavedChangesModal(true);
      } else {
        handleRemoveShipment(returnManifestId, shipmentId);
      }
    },
    [editingShipments, handleRemoveShipment],
  );

  const handleCloseUnsavedChangesModal = () => {
    setShowUnsavedChangesModal(false);
  };

  const extendedColumns = useMemo(() => {
    const removeColumn = {
      accessorKey: 'none',
      header: '',
      enableSorting: false,
      meta: {
        cellProps: { className: 'text-center' },
      },
      cell: ({ row: { original } }) => {
        const shipmentId = original.id;

        return (
          <FontAwesomeIcon
            className="cursor-pointer"
            icon="trash-alt"
            onClick={() => checkAndRemoveShipment(returnManifestId, shipmentId)}
          />
        );
      },
    };

    return [...RETURN_MANIFEST_DETAIL_SHIPMENT_COLUMNS, removeColumn];
  }, [returnManifestId, checkAndRemoveShipment]);

  const tableConfig = useMemo(() => {
    return {
      autoExpandAll: true,
    };
  }, []);

  return (
    <div className="position-relative">
      <Table
        ref={tableRef}
        title={`Return Manifests #${returnManifestId}`}
        apiService={returnManifestApi}
        columns={extendedColumns}
        fetchConfig={{
          method: 'getShipmentsOfReturnManifest',
          transformParams: params => {
            const transformedParams = new URLSearchParams();
            if (params.pageIndex !== undefined) {
              transformedParams.append('page', params.pageIndex + 1);
            }
            if (params.pageSize) {
              transformedParams.append('page_size', params.pageSize);
            }

            return transformedParams;
          },
        }}
        resourceId={returnManifestId}
        initialPageSize={200}
        renderExpandedRow={row => (
          <ReturnManifestShipmentExpandedRow shipment={row.original} />
        )}
        onDataChange={handleDataUpdate}
        tableConfig={tableConfig}
      />

      {!returnManifestStatus ||
      returnManifestStatus === 'closed' ||
      returnManifestStatus === 'discarded' ? null : (
        <ReturnManifestShipmentSummaryPanel
          returnManifestId={returnManifestId}
          onReturnManifestUpdate={refreshReturnManifestData}
          shipmentCount={totalShipments}
        />
      )}

      <UnsavedChangesModal
        show={showUnsavedChangesModal}
        onHide={handleCloseUnsavedChangesModal}
        message="There are unsaved changes in other shipments."
      />
    </div>
  );
};

export default ReturnManifestShipment;
